import { useState, useEffect } from 'react';
import { X, Flag } from '@phosphor-icons/react';

export default function GpsScorecard({ open, onClose, currentHole, onNavigateHole, totalHoles, courseName }) {
  const [scores, setScores] = useState(() => Array.from({ length: totalHoles }, () => 0));

  useEffect(() => {
    setScores(prev => {
      if (prev.length === totalHoles) return prev;
      const next = Array.from({ length: totalHoles }, (_, i) => prev[i] || 0);
      return next;
    });
  }, [totalHoles]);

  const updateScore = (idx, val) => {
    setScores(prev => prev.map((s, i) => (i === idx ? val : s)));
  };

  const increment = (idx) => updateScore(idx, Math.min(scores[idx] + 1, 15));
  const decrement = (idx) => updateScore(idx, Math.max(scores[idx] - 1, 0));

  const frontNine = scores.slice(0, 9).reduce((a, b) => a + b, 0);
  const backNine = scores.slice(9, 18).reduce((a, b) => a + b, 0);
  const total = scores.reduce((a, b) => a + b, 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col" data-testid="gps-scorecard-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0B1F14]/95 backdrop-blur-md" onClick={onClose} />

      {/* Content */}
      <div className="relative flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-6 pb-4">
          <div>
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-sans font-semibold">Scorecard</p>
            <h2 className="text-white text-lg font-serif font-semibold mt-0.5" data-testid="scorecard-modal-title">
              {courseName || 'Round in Progress'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"
            data-testid="scorecard-close-btn"
          >
            <X size={18} className="text-white" />
          </button>
        </div>

        {/* Summary bar */}
        <div className="flex items-center justify-between px-5 py-3 border-y border-white/10">
          <div className="text-center flex-1">
            <p className="text-[9px] text-white/40 uppercase tracking-wider font-sans">Front</p>
            <p className="text-white text-lg font-serif font-semibold">{frontNine || '--'}</p>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div className="text-center flex-1">
            <p className="text-[9px] text-white/40 uppercase tracking-wider font-sans">Back</p>
            <p className="text-white text-lg font-serif font-semibold">{backNine || '--'}</p>
          </div>
          <div className="w-px h-8 bg-white/10" />
          <div className="text-center flex-1">
            <p className="text-[9px] text-[#D4AF37] uppercase tracking-wider font-sans font-semibold">Total</p>
            <p className="text-[#D4AF37] text-xl font-serif font-bold" data-testid="scorecard-total">{total || '--'}</p>
          </div>
        </div>

        {/* Holes grid - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar">
          <div className="grid grid-cols-3 gap-2">
            {scores.map((score, idx) => {
              const isCurrentHole = idx === currentHole;
              return (
                <div
                  key={idx}
                  role="button"
                  tabIndex={0}
                  onClick={() => onNavigateHole(idx)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateHole(idx); }}
                  className={`relative rounded-lg p-3 transition-all duration-200 cursor-pointer ${
                    isCurrentHole
                      ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/40 ring-1 ring-[#D4AF37]/20'
                      : score > 0
                        ? 'bg-white/8 border border-white/10'
                        : 'bg-white/4 border border-white/5'
                  }`}
                  data-testid={`scorecard-hole-${idx + 1}`}
                >
                  {/* Hole number */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-sans font-semibold uppercase tracking-wider ${
                      isCurrentHole ? 'text-[#D4AF37]' : 'text-white/40'
                    }`}>
                      Hole {idx + 1}
                    </span>
                    {isCurrentHole && (
                      <Flag size={10} weight="fill" className="text-[#D4AF37]" />
                    )}
                  </div>

                  {/* Score display + controls */}
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); decrement(idx); }}
                      className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 active:scale-90 transition-all"
                      data-testid={`score-decrement-${idx + 1}`}
                    >
                      <span className="text-sm font-bold leading-none">-</span>
                    </button>
                    <span className={`text-2xl font-serif font-bold min-w-[28px] text-center ${
                      score > 0 ? 'text-white' : 'text-white/20'
                    }`} data-testid={`score-value-${idx + 1}`}>
                      {score || '-'}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); increment(idx); }}
                      className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 active:scale-90 transition-all"
                      data-testid={`score-increment-${idx + 1}`}
                    >
                      <span className="text-sm font-bold leading-none">+</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Front/Back 9 divider labels */}
          {totalHoles >= 18 && (
            <div className="mt-4 flex items-center gap-3 px-1">
              <div className="flex-1 h-px bg-white/10" />
              <p className="text-[9px] text-white/30 uppercase tracking-wider font-sans">18 Holes</p>
              <div className="flex-1 h-px bg-white/10" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
